// El origen de datos del demo público: las ocho operaciones de backend de
// MaterialAiGenerate, servidas desde una captura en vez de la API.
//
// Se inyecta con `<MaterialAiGenerate :data-source="…">`. El componente que monta
// el demo es EL MISMO que usa un usuario logueado —no una copia—, así que lo que
// se ve acá es lo que se compra. La versión anterior del demo heredaba del
// componente y sobrescribía métodos internos; cuando el componente se reescribió,
// los overrides dejaron de interceptar y el demo pasó a postear contra la API de
// verdad sin que nada avisara. Por eso la superficie es esta lista corta y
// declarada, y por eso `crearOrigenDemo` verifica que estén las ocho.

import { OPERACIONES_BACKEND } from "./MaterialAiGenerate.vue";

/** Espera simulada: sin esto el stream aparece de golpe y no se ve el producto. */
const dormir = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Cuánto tarda cada tramo. Una generación real toma varios segundos por tarjeta;
 * reproducir eso entero aburre, y saltárselo hace que el demo no se parezca al
 * producto. Estos valores dejan ver la animación sin que nadie se vaya.
 */
const RITMO = {
  listas: 180,
  pregunta: 900,
  planificacion: 1200,
  guardado: 700,
};

/**
 * Empareja cada `item` que pide el componente con la pregunta capturada.
 *
 * El componente genera de a una y en el orden del plan, así que basta con una
 * cola por (OA, indicador) más una cola global de respaldo. Se hace por consumo y
 * no por búsqueda repetida para que dos actividades del MISMO indicador —que el
 * wizard permite— devuelvan preguntas distintas y no la misma dos veces, que es
 * justo lo que el demo tiene que evitar mostrar.
 */
function crearCola(fixture) {
  const plan = fixture.plan || [];
  const questions = fixture.questions || [];
  const porLlave = new Map();
  // Segundo índice sólo por OA. Hace falta porque el wizard pide de dos maneras:
  // por indicador (`item.subtag`) y por peldaño (`item.peldano`, sin subtag). En
  // el segundo caso la llave compuesta nunca acierta, y sin este índice la tarjeta
  // caía en la cola global y podía traer el ejercicio de OTRO OA — con el rótulo
  // del pedido encima, o sea el demo diciendo «MA03 OA 02» sobre un ejercicio de
  // MA03 OA 01. En una pantalla de venta eso es peor que repetir.
  const porOa = new Map();
  const global = [];

  questions.forEach((question, index) => {
    const entry = plan[index] || {};
    const oaId = entry.oaId || question.oaId || "";
    const llave = `${oaId}|${entry.indicatorId || question.indicatorId || ""}`;
    if (!porLlave.has(llave)) porLlave.set(llave, []);
    porLlave.get(llave).push(question);
    if (!porOa.has(oaId)) porOa.set(oaId, []);
    porOa.get(oaId).push(question);
    global.push(question);
  });

  // Copia intacta para poder recargar: el wizard puede pedir MÁS actividades de
  // las que tiene la captura —dos OAs con los cuatro peldaños por defecto son
  // ocho, y una captura típica trae seis— y una tarjeta sin pregunta se queda en
  // blanco. Repetir un ejercicio es mucho menos malo que mostrar un hueco.
  const todas = questions.slice();

  return {
    tomar(item) {
      const oaId = (item && item.oa && item.oa.id) || "";
      const indicatorId = (item && item.subtag && item.subtag.id) || "";
      const candidatas =
        porLlave.get(`${oaId}|${indicatorId}`) || porLlave.get(`${oaId}|`) || [];
      // Orden de preferencia: OA+indicador exacto → cualquiera del mismo OA →
      // cualquiera. Sólo se cruza de OA cuando ese OA ya no tiene nada.
      const delMismoOa = porOa.get(oaId) || [];
      let elegida =
        candidatas.shift() ||
        delMismoOa[0] ||
        global.shift();
      if (elegida && delMismoOa.length) {
        // Se recicla dentro del OA en vez de agotarlo: varios peldaños del mismo
        // OA deben poder mostrar algo, y lo capturado para ese OA es lo correcto.
        delMismoOa.push(delMismoOa.shift());
      }
      if (elegida) {
        // Sacarla también de la otra cola, para no servirla dos veces.
        const i = global.indexOf(elegida);
        if (i >= 0) global.splice(i, 1);
      } else if (todas.length) {
        // Se acabó lo capturado: se vuelve a empezar en vez de devolver null.
        global.push(...todas.slice(1));
        elegida = todas[0];
      }
      return elegida || null;
    },
    quedan() {
      return global.length;
    },
  };
}

/**
 * La URL del PDF, o `null` si en este entorno no está.
 *
 * El fixture está versionado y el PDF no (va en `.gitignore`, como los binarios
 * del demo de Planificaciones). Las dos mitades viajan por caminos distintos: el
 * fixture llega con el deploy y el PDF sólo existe si alguien corrió la captura
 * EN ese servidor. Cuando falta, el componente igual pinta el botón de descarga y
 * el visitante se lleva un 404 — que es justo lo que le pasó al demo de
 * Planificaciones el 21-ago con sus imágenes.
 *
 * Comprobarlo cuesta un HEAD y deja el demo coherente: sin PDF muestra el
 * material terminado y no ofrece lo que no puede entregar. NO repone el archivo:
 * para eso hay que correr la captura en el servidor (runbook en
 * public/static/demo-materials/README.md).
 */
async function pdfSiExiste(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { method: "HEAD" });
    if (res.ok) return url;
    console.warn(
      "[materialsDemoDataSource] el PDF del demo no está en este entorno (%s). " +
        "Corré `capturar_demo_materiales` en este servidor.",
      res.status
    );
  } catch (error) {
    // Red caída o CORS: no se puede afirmar que falte, pero tampoco ofrecerlo.
    console.warn("[materialsDemoDataSource] no pude verificar el PDF:", error);
  }
  return null;
}

/**
 * Los OAs que el demo puede ofrecer: sólo los que tienen pregunta capturada.
 *
 * El wizard trae los 26 OAs del curso porque así los devuelve la API, pero la
 * captura cubre unos pocos —y menos todavía si el validador del motor rechazó
 * alguno—. Si el visitante elige uno sin pregunta, la tarjeta se queda vacía y el
 * demo parece roto justo en el momento en que tiene que convencer. Ofrecer sólo
 * lo capturado hace que cualquier camino que tome termine en un ejercicio real.
 *
 * Los indicadores se recortan igual, por el mismo motivo.
 */
function oasOfrecibles(fixture) {
  const plan = fixture.plan || [];
  const oas = (fixture.wizard && fixture.wizard.oas) || [];
  const indicadoresPorOa = new Map();
  plan.forEach((entry) => {
    if (!entry.oaId) return;
    if (!indicadoresPorOa.has(entry.oaId)) indicadoresPorOa.set(entry.oaId, new Set());
    if (entry.indicatorId) indicadoresPorOa.get(entry.oaId).add(entry.indicatorId);
  });
  if (!indicadoresPorOa.size) return oas;

  return oas
    .filter((oa) => indicadoresPorOa.has(oa.id))
    .map((oa) => {
      const permitidos = indicadoresPorOa.get(oa.id);
      const indicators = (oa.indicators || []).filter((i) => permitidos.has(i.id));
      // Un OA capturado sin indicador (el wizard lo permite) conserva su lista:
      // vaciarla lo dejaría sin nada que marcar.
      return indicators.length ? { ...oa, indicators } : oa;
    });
}

// ── de la forma del fixture a la forma de la API ─────────────────────────────
//
// `capturar_demo_materiales` guarda las tres listas del wizard tal como quedan
// DESPUÉS de que el componente las transforma —`{code, original_name}` para los
// cursos, `{code, enum_code, name}` para las asignaturas, `indicators` con su
// `label` ya armado—, porque así son legibles al auditar la captura.
//
// Pero el componente vuelve a transformar lo que recibe: `fetchGrades` espera
// `{grades: [[code, nombre]]}`, `getSubjects` lee `s.id` (no `s.code`) y
// `goToOas` lee `oa.subtags` con `summary`. Devolver la forma guardada tal cual
// hace que el wizard quede con los selectores VACÍOS, que es un fallo mudo: no
// hay error, simplemente no hay nada que elegir.
//
// Se adapta acá, en un solo lugar, en vez de cambiar el formato de la captura:
// el `captura.json` lo leen también el juez y quien audita a mano.

const crudoGrade = (g) => [g.code, g.original_name];

const crudoSubject = (s) => ({
  id: s.code,
  enum_code: s.enum_code,
  name: s.name,
});

/** "Ind. 3: cuentan de 2 en 2" → "cuentan de 2 en 2" */
const resumenDeIndicador = (indicador) =>
  String(indicador.label || "").replace(/^\s*Ind\.\s*\d+\s*:\s*/, "") ||
  indicador.description ||
  "";

const crudoOa = (oa, i) => ({
  id: oa.id,
  name: oa.name,
  description: oa.description,
  // El orden ya viene del catálogo; `priority` sólo tiene que ser estable.
  priority: i,
  resource_type: oa.resource_type || 0,
  resource_type_display: oa.resource_type_display || "",
  // `summary` no puede ser null: el componente filtra por eso y un indicador
  // sin resumen desaparece de la lista.
  subtags: (oa.indicators || []).map((ind) => ({
    id: ind.id,
    summary: resumenDeIndicador(ind),
    name: ind.description || ind.label || "",
  })),
});

/**
 * Construye el origen de datos del demo.
 *
 * @param {object} fixture  lo que exporta materialsDemoFixture.
 * @param {object} [opciones]
 * @param {boolean} [opciones.instantaneo]  sin esperas (para tests).
 */
export function crearOrigenDemo(fixture, opciones = {}) {
  const ritmo = opciones.instantaneo
    ? { listas: 0, pregunta: 0, planificacion: 0, guardado: 0 }
    : RITMO;
  const wizard = fixture.wizard || {};
  const cola = crearCola(fixture);

  const origen = {
    // Curso y asignatura: sólo los capturados.
    //
    // El wizard trae las listas completas porque así las devuelve la API, pero la
    // captura cubre UNA combinación. Ofrecer las catorce dejaría al visitante
    // elegir «1° medio · Música» y recibir igual los OAs de 3° básico, que es
    // peor que no dejarlo elegir: el demo se contradice a sí mismo justo cuando
    // tiene que dar confianza. Mismo criterio que `oasOfrecibles`.
    getGrades: async () => {
      await dormir(ritmo.listas);
      const grades = wizard.grades || [];
      const elegido = grades.find((g) => g.code === wizard.gradeCode);
      return { grades: (elegido ? [elegido] : grades).map(crudoGrade) };
    },

    getSubjects: async () => {
      await dormir(ritmo.listas);
      const subjects = wizard.subjects || [];
      const elegida = subjects.find((s) => s.code === wizard.subjectCode);
      return (elegida ? [elegida] : subjects).map(crudoSubject);
    },

    getOas: async () => {
      await dormir(ritmo.listas);
      return oasOfrecibles(fixture).map(crudoOa);
    },

    generateVisualMathQuestions: async (payload) => {
      await dormir(ritmo.pregunta);
      const item = (payload.items || [])[0];
      const question = cola.tomar(item);
      // `avisos` viaja aunque esté vacío: el componente lo lee sin comprobar y
      // devolver `undefined` lo haría reventar en una rama que nadie prueba.
      return { questions: question ? [question] : [], avisos: [] };
    },

    generatePageQuestions: async (payload) => {
      await dormir(ritmo.pregunta);
      const item = (payload.items || [])[0];
      const question = cola.tomar(item);
      return { questions: question ? [question] : [], avisos: [] };
    },

    generateReadingText: async () => {
      await dormir(ritmo.pregunta);
      // Las capturas de Matemáticas no traen textos de lectura. Devolver una
      // forma válida y vacía es preferible a fallar: si algún día se captura
      // Lenguaje, el campo aparece solo y esto lo sirve.
      return fixture.readingText || { id: null, text: "", title: "" };
    },

    planificarMaterialProgresion: async () => {
      await dormir(ritmo.planificacion);
      return fixture.progresion || { plan: [] };
    },

    createMaterial: async () => {
      await dormir(ritmo.guardado);
      const material = fixture.material || {};
      const downloads = fixture.downloads || {};
      // Nada se persiste: el demo no crea materiales ni consume usos. El PDF que
      // se ofrece es el que dejó la captura, o sea el que produjo esta misma
      // corrida — no un archivo de muestra escrito aparte.
      return {
        name: material.name || "Material de ejemplo",
        pdf: await pdfSiExiste(downloads.pdf),
        codigo_hoja: material.codigo_hoja || null,
      };
    },
  };

  const faltantes = OPERACIONES_BACKEND.filter((nombre) => !origen[nombre]);
  if (faltantes.length) {
    // Ruidoso a propósito. Una operación que falta no rompe nada visible: cae al
    // backend real, o sea el demo empieza a pegarle a la API de producción sin
    // sesión. Eso es exactamente lo que pasó con el demo anterior.
    console.error(
      "[materialsDemoDataSource] faltan operaciones y van a ir al backend REAL:",
      faltantes
    );
  }

  return origen;
}

export default crearOrigenDemo;
